import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useAlert from '../../../../../state/hooks/useAlert';
import useToast from '../../../../../state/hooks/useToast';
import { changePageInfo } from '../../../../../state/store/reducers/ui/headerSlice';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import appProviderService from '../../../../../services/api/apps/appProviderService';
import { Button, Pagination } from '../../../../../components/common';
import { ListState } from '../../../../../components/blocks';
import { paginationInfo } from '../../../../../utils/pagination';
import { format } from 'date-fns';
import clsx from 'clsx';
import AppProvidersListFilters from './AppProvidersListFilters';
import ModalAppProviderDetails from '../modals/ModalAppProviderDetails';
import ModalAppProviderForm from '../modals/ModalAppProviderForm';


function AppProvidersListPage() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    const [providerForm, setProviderForm] = useState({ open: false, provider: null });
    const [providerDetails, setProviderDetails] = useState({ open: false, provider: null });



    // get query params
    const query: any = {
        pager: {
            page: Number(searchParams.get('page')) || 1,
        },
        filters: {},
    };
    if (searchParams.get('appCategoryId')) { query.filters['appCategoryId'] = searchParams.get('appCategoryId') || '' }
    //if (searchParams.get('status')) { query.filters['status'] = searchParams.get('status') || '' }


    useEffect(() => {
        dispatch(changePageInfo({ title: "App Categories" }));
    }, []);


    const queryKey = ['payments', 'apps', 'providers', query];
    const queryClient = useQueryClient();
    const dataQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => appProviderService.getProviders({ ...query.pager, ...query.filters }),
        staleTime: Infinity,
        retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: false, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });


    return (
        <div>
            <div className='flex flex-row-reverse flex-wrap justify-between items-start mb-1'>
                <Button type='button' onClick={() => setProviderForm({ open: true, provider: null })} className='mb-4'>
                    Create Category
                </Button>

                <AppProvidersListFilters
                    initialData={query.filters}
                    onSearch={(values) => {
                        setSearchParams({ ...values, page: 1 });
                        /*  setSearchParams(current => {
                             const entries = { ...Object.fromEntries(current.entries()), ...values };
                             if (entries.page) {
                                 entries.page = '1';
                             }
                             return entries;
                         }); */
                    }}
                    searching={dataQuery.isLoading}
                />
            </div>

            {
                !dataQuery.isError && dataQuery.isFetched && dataQuery.isFetched && dataQuery.data?.data && dataQuery.data?.data.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 px-6'>Name</th>
                                    <th className='py-6 px-6'>Description</th>
                                    <th className='py-6 px-6'>Web Address</th>
                                    <th className='py-6 px-6'>Categories</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Date Created</th>
                                    {/*  <th className='py-6 px-6 text-right'>Action</th> */}
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    dataQuery.data?.data.map((dataItem: any) =>
                                        <tr key={dataItem.id}>
                                            <td className='py-4 px-6'>
                                                <button type='button' onClick={() => setProviderDetails({ open: true, provider: dataItem })} className='transition-colors duration-300 hocus:text-blue'>
                                                    {dataItem.name}
                                                </button>
                                            </td>
                                            <td className='py-4 px-6' title={dataItem.description}>
                                                <p className='line-clamp-1'>{dataItem.description}</p>
                                            </td>
                                            <td className='py-4 px-6'>
                                                <div className='flex gap-2'>
                                                    {
                                                        dataItem?.appCategories
                                                            ?.slice(0, 2)
                                                            ?.map((category: any, paymentMethodIndex: number) =>
                                                                <span key={category.id} className='text-sm whitespace-nowrap bg-black/5 dark:bg-white/5 rounded-lg py-1.5 px-2.5'>
                                                                    {category?.name}
                                                                </span>
                                                            )
                                                    }
                                                    {
                                                        dataItem?.appCategories?.length > 2 &&
                                                        <span className='text-sm bg-black/5 dark:bg-white/5 rounded-lg py-1.5 px-2.5'>
                                                            +{dataItem?.appCategories?.length - 2}
                                                        </span>
                                                    }
                                                    {/* {
                                                        feedItem.permissions.length > 0 &&
                                                        <button type='button' onClick={() => setUserRoleDetails({ open: true, role: roleItem })} className='opacity-0 group-hover:opacity-90 font-medium whitespace-nowrap text-sm transition-all duration-300 hover:text-blue p-2 mx-1'>
                                                            {roleItem.permissions.length > 2 ? 'View All' : 'View'}
                                                        </button>
                                                    } */}
                                                </div>
                                            </td>
                                            <td className='py-4 px-6'>{dataItem.webAddress}</td>
                                            <td className='py-4 px-6'>
                                                <span className={clsx('text-sm font-medium rounded-lg py-1.5 px-2.5', {
                                                    'bg-green-600 text-white': dataItem.status === 'ACTIVE',
                                                    'bg-yellow-600 text-white': dataItem.status === 'INACTIVE',
                                                })}>
                                                    {dataItem.status}
                                                </span>
                                            </td>
                                            <td className='py-4 px-6 whitespace-nowrap'>{format(new Date(dataItem.createdDate), 'd LLL yyyy h:mm a')}</td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className='flex justify-between flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(dataQuery.data.pager.pageSize, dataQuery.data.pager.totalCount, dataQuery.data?.pager.page)}</p>

                        <Pagination
                            currentPage={dataQuery.data?.pager.page}
                            totalCount={dataQuery.data?.pager.totalCount}
                            pageSize={dataQuery.data.pager.pageSize}
                            onPageChange={(page) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    page: page.toString(),
                                }));
                            }}
                        />
                    </div>
                </React.Fragment>
            }


            <ListState
                hasSearch={Object.keys(query.filters).length > 0}
                listCount={dataQuery.data?.data.length || 0}
                totalDataCount={dataQuery.data?.pager.total || 0}
                isLoading={dataQuery.isLoading}
                isError={dataQuery.isError}
                onTryAgain={() => dataQuery.refetch()}
            />

            <ModalAppProviderDetails
                open={providerDetails.open}
                provider={providerDetails.provider}
                onClose={() => setProviderDetails((prevState) => ({ ...prevState, open: false }))}
            />


            <ModalAppProviderForm
                open={providerForm.open}
                provider={providerForm.provider}
                onAdded={(data) => {
                    queryClient.invalidateQueries({ queryKey });
                    addToast({
                        text: `Provider created successfully`,
                        type: "success"
                    });
                }}
                onUpdated={(data) => {
                    addToast({
                        text: `Provider updated successfully`,
                        type: "success"
                    });
                }}
                onClose={() => setProviderForm((prevState) => ({ ...prevState, open: false }))}
            />
        </div>
    );
}

export default AppProvidersListPage;